import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, AlertTriangle, CheckCircle, BarChart3,
  Calendar, TrendingUp
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import useTokenValidation from 'hooks/useTokenValidation';
import api from 'services/api';
import { CompanyChangeBlur } from 'components/common';
import PageLayout from 'components/common/PageLayout';
import AgenteIAOperacional from 'features/operacional/components/AgenteIAOperacional';
import CentroAgendamento from 'features/operacional/components/CentroAgendamento';
import ossEstoqueService from 'features/estoque/services/ossEstoqueService';
import { useAuth } from 'contexts/AuthContext';

const EMPTY_PERMISSIONS = [];

const Operacional = () => {
  // Validação automática de token
  useTokenValidation();
  const { user } = useAuth();
  const userPermissions = user?.permissions ?? EMPTY_PERMISSIONS;
  const hasPermission = useCallback(
    (permissionName) => userPermissions.some((permission) => permission.name === permissionName),
    [userPermissions]
  );
  const [dadosAgendamentos, setDadosAgendamentos] = useState({});
  const [dadosOS, setDadosOS] = useState({});
  // Verificar se tem alguma permissão operacional
  const temPermissaoOperacional = () => {
    return hasPermission('operacional.view') ||
           hasPermission('operacional.agendamentos.view') ||
           hasPermission('operacional.edit');
  };

  // Determinar aba inicial baseada nas permissões
  const getAbaInicial = () => {
    if (hasPermission('operacional.agendamentos.view')) return 'agendamento';
    if (hasPermission('operacional.view')) return 'overview';
    return 'overview'; // fallback
  };

  const [abaAtiva, setAbaAtiva] = useState(getAbaInicial()); // 'overview' ou 'agendamento'

  const carregarDadosOperacionais = useCallback(async () => {
    try {
      // Somente buscar o que o usuário tem permissão
      if (hasPermission('operacional.agendamentos.view')) {
        const responseAgendamentos = await api.get('/operacional/agendamentos', {
          params: {
            data_inicio: '2025-01-01',
            data_fim: '2025-06-30'
          }
        });
        if (responseAgendamentos.data?.success) {
          setDadosAgendamentos(responseAgendamentos.data.data);
        }
      }

      // A Visão Geral exibe estes números nos cards e no Agente IA. A leitura vem do fluxo de OSS
      // do estoque, mas quem a consome é o Operacional — por isso a permissão que a libera é a da
      // aba, e não mais a de estoque, que deixou de ter relação com esta página.
      if (hasPermission('operacional.view')) {
        const result = await ossEstoqueService.buscarPorDia({
          data_inicio: '2025-01-01',
          data_fim: '2025-06-30'
        });
        if (result?.success) {
          setDadosOS(result.data);
        }
      }

    } catch (error) {
      console.error('❌ Erro ao carregar dados operacionais:', error);
      
      // Em caso de erro, usar dados vazios
      setDadosAgendamentos({});
      setDadosOS({});
    } finally {
      // Disparar evento quando loading termina
      window.dispatchEvent(new CustomEvent('company-change-complete'));
    }
  }, [hasPermission]);

  useEffect(() => {
    carregarDadosOperacionais();
  }, [carregarDadosOperacionais]);

  // Listener para mudanças de empresa
  useEffect(() => {
    const handleCompanyChange = (event) => {
      const { company } = event.detail || {};
      if (company) {
        // Recarregar dados quando empresa muda
        carregarDadosOperacionais();
      }
    };

    // Escutar mudanças de empresa
    window.addEventListener('company-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [carregarDadosOperacionais]);

  // Verificar se tem permissão operacional
  if (!temPermissaoOperacional()) {
    return (
      <PageLayout>
        <div className="min-h-screen bg-[color:var(--surface-muted)] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[color:var(--text-primary)] mb-2">Acesso Negado</h1>
            <p className="text-[color:var(--text-muted)]">Você não tem permissão para acessar a área operacional.</p>
            <button
              onClick={() => window.history.back()}
              className="mt-4 bg-[color:var(--accent)] text-white px-4 py-2 rounded hover:opacity-90"
            >
              Voltar
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Removido LoadingSpinner para permitir blur do CompanyChangeBlur

  return (
    <>
      <CompanyChangeBlur />
      <PageLayout 
        showRefreshButton={true}
        customRefreshFunction={carregarDadosOperacionais}
      >
        <div className="w-full p-6">

        {/* Sistema de Abas */}
        <div className="mb-8">
          <div className="border-b border-[color:var(--border)]">
            <nav className="-mb-px flex space-x-8">
              {hasPermission('operacional.view') && (
                <button
                  onClick={() => setAbaAtiva('overview')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    abaAtiva === 'overview'
                      ? 'border-[color:var(--accent)] text-[color:var(--accent)]'
                      : 'border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:border-[color:var(--border)]'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="h-4 w-4" />
                    <span>Visão Geral</span>
                  </div>
                </button>
              )}
              
              {hasPermission('operacional.agendamentos.view') && (
                <button
                  onClick={() => setAbaAtiva('agendamento')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    abaAtiva === 'agendamento'
                      ? 'border-[color:var(--accent)] text-[color:var(--accent)]'
                      : 'border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:border-[color:var(--border)]'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Centro de Agendamento</span>
                  </div>
                </button>
              )}
              
            </nav>
          </div>
        </div>

        {/* Conteúdo das Abas */}
        {abaAtiva === 'overview' && (
          <>
            {/* Cards de Agendamentos */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              <div className="bg-[color:var(--surface)] p-6 rounded-lg shadow-sm border border-[color:var(--border)]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Total Agendamentos</p>
                    <p className="text-2xl font-bold text-[color:var(--accent)]">
                      {dadosAgendamentos?.resumo?.total_agendamentos?.toLocaleString('pt-BR') || '0'}
                    </p>
                    <p className="text-xs text-[color:var(--accent)]">Período atual</p>
                  </div>
                  <div className="p-3 bg-[color:var(--surface-muted)] rounded-lg">
                    <Calendar className="h-6 w-6 text-[color:var(--accent)]" />
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-6 rounded-lg shadow-sm border border-[color:var(--border)]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Taxa de Conclusão</p>
                    <p className="text-2xl font-bold text-[color:var(--status-good)]">
                      {dadosAgendamentos?.resumo?.taxa_conclusao || 0}%
                    </p>
                    <p className="text-xs text-[color:var(--status-good)]">Excelente performance</p>
                  </div>
                  <div className="p-3 bg-[color:var(--surface-muted)] rounded-lg">
                    <CheckCircle className="h-6 w-6 text-[color:var(--status-good)]" />
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-6 rounded-lg shadow-sm border border-[color:var(--border)]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">OS Abertas</p>
                    <p className="text-2xl font-bold text-[color:var(--status-warn)]">
                      {dadosOS?.resumo?.total_os_abertas?.toLocaleString('pt-BR') || '0'}
                    </p>
                    <p className="text-xs text-[color:var(--status-warn)]">Requer atenção</p>
                  </div>
                  <div className="p-3 bg-[color:var(--surface-muted)] rounded-lg">
                    <Settings className="h-6 w-6 text-[color:var(--status-warn)]" />
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-6 rounded-lg shadow-sm border border-[color:var(--border)]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Taxa Resolução OS</p>
                    <p className="text-2xl font-bold text-[color:var(--accent)]">
                      {dadosOS?.resumo?.taxa_resolucao || 0}%
                    </p>
                    <p className="text-xs text-[color:var(--accent)]">
                      {dadosOS?.resumo?.tempo_medio_resolucao || 0} dias médio
                    </p>
                  </div>
                  <div className="p-3 bg-[color:var(--surface-muted)] rounded-lg">
                    <TrendingUp className="h-6 w-6 text-[color:var(--accent)]" />
                  </div>
                </div>
              </div>
            </div>

            {/* Gráficos e Análises */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Gráfico de Agendamentos */}
              <div className="bg-[color:var(--surface)] p-6 rounded-lg shadow-sm border border-[color:var(--border)]">
                <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
                  <Calendar className="h-5 w-5 text-[color:var(--accent)] mr-2" />
                  Agendamentos - Últimos 7 Dias
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={dadosAgendamentos?.ultimos_7_dias || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="data" tick={{ fill: 'var(--text-muted)' }} />
                    <YAxis tick={{ fill: 'var(--text-muted)' }} />
                    <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} labelStyle={{ color: 'var(--text-primary)' }} />
                    <Line type="monotone" dataKey="agendados" stroke="var(--accent)" strokeWidth={2} />
                    <Line type="monotone" dataKey="concluidos" stroke="var(--status-good)" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Gráfico de OS */}
              <div className="bg-[color:var(--surface)] p-6 rounded-lg shadow-sm border border-[color:var(--border)]">
                <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
                  <Settings className="h-5 w-5 text-[color:var(--status-warn)] mr-2" />
                  OS - Últimos 15 Dias
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={dadosOS?.ultimos_15_dias || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="data" tick={{ fill: 'var(--text-muted)' }} />
                    <YAxis tick={{ fill: 'var(--text-muted)' }} />
                    <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} labelStyle={{ color: 'var(--text-primary)' }} />
                    <Bar dataKey="abertas" fill="var(--status-warn)" />
                    <Bar dataKey="fechadas" fill="var(--status-good)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Distribuições */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Agendamentos por Tipo */}
              <div className="bg-[color:var(--surface)] p-6 rounded-lg shadow-sm border border-[color:var(--border)]">
                <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
                  <BarChart3 className="h-5 w-5 text-[color:var(--accent)] mr-2" />
                  Agendamentos por Tipo
                </h3>
                <div className="space-y-4">
                  {dadosAgendamentos?.por_tipo?.map((tipo, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-20 text-sm text-[color:var(--text-muted)]">{tipo.tipo}</div>
                      <div className="flex-1 mx-4">
                        <div className="w-full bg-[color:var(--surface-muted)] rounded-full h-2">
                          <div 
                            className="bg-[color:var(--accent)] h-2 rounded-full transition-all duration-500"
                            style={{ width: `${tipo.percentual}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="w-12 text-sm text-[color:var(--text-primary)] font-medium text-right">
                        {tipo.quantidade}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* OS por Prioridade */}
              <div className="bg-[color:var(--surface)] p-6 rounded-lg shadow-sm border border-[color:var(--border)]">
                <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
                  <AlertTriangle className="h-5 w-5 text-[color:var(--status-warn)] mr-2" />
                  OS por Prioridade
                </h3>
                <div className="space-y-4">
                  {dadosOS?.por_prioridade?.map((prioridade, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-[color:var(--surface-muted)] rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          prioridade.prioridade === 'Crítica' ? 'bg-[color:var(--status-bad)]' :
                          prioridade.prioridade === 'Alta' ? 'bg-[color:var(--status-warn)]' :
                          prioridade.prioridade === 'Média' ? 'bg-[color:var(--status-warn)]' : 'bg-[color:var(--status-good)]'
                        }`}></div>
                        <span className="font-medium text-[color:var(--text-primary)]">{prioridade.prioridade}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[color:var(--text-primary)]">
                          {prioridade.pendentes} pendentes
                        </p>
                        <p className="text-sm text-[color:var(--text-muted)]">
                          {prioridade.fechadas} resolvidas
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Agente IA Operacional */}
            <div className="mb-8">
              <AgenteIAOperacional 
                dadosAgendamentos={dadosAgendamentos}
                dadosOS={dadosOS}
              />
            </div>
          </>
        )}

        {/* Aba do Centro de Agendamento */}
        {abaAtiva === 'agendamento' && hasPermission('operacional.agendamentos.view') && (
          <CentroAgendamento />
        )}

        </div>
      </PageLayout>
    </>
  );
};

export default Operacional; 
